import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { Button, Input, space, Checkbox } from '@ticketswap/solar'
import { useMutation } from '@apollo/react-hooks'
import { Query, Mutation } from '@apollo/react-components'
import gql from 'graphql-tag'
import { Formik } from 'formik'
import * as yup from 'yup'
import { GET_VIEWER } from '~/lib/get-viewer'
import { track } from '~/utils'
import cookies from 'js-cookie'
import {
  AUTHENTICATION_COMPLETED,
  VERIFICATION_STARTED,
} from '~/lib/tracking-events'
import { useViewer } from '../ViewerContext'

const START_EMAIL_VERIFICATION = gql`
  mutation startUserEmailVerification(
    $input: StartUserEmailVerificationInput!
  ) {
    startUserEmailVerification(input: $input) {
      errors {
        key
        message
        code
      }
    }
  }
`

export const AGREE_WITH_TERMS_AND_PRIVACY_POLICY = gql`
  mutation agreeWithTermsAndPrivacyPolicy {
    AgreeWithTermsAndPrivacyPolicy {
      user {
        id
        hasAgreedToLatestTermsAndPrivacyPolicy
      }
      errors {
        key
        message
        code
      }
    }
  }
`

const GIVE_CONSENT = gql`
  mutation giveConsent($input: GiveConsentInput!) {
    giveConsent(input: $input) {
      errors {
        message
        code
      }
    }
  }
`

const StyledCheckbox = styled(Checkbox)`
  margin-top: ${space[16]};
`

const StyledButton = styled(Button)`
  margin-top: ${space[24]};
`

function SignupSocial({ t, onSignupComplete, hide }) {
  const [loading, setLoading] = useState(false)
  const [marketingConsentGiven, setMarketingConsentGiven] = useState(false)
  const { setViewer } = useViewer()
  const [giveConsentMutation] = useMutation(GIVE_CONSENT)

  useEffect(() => {
    track(AUTHENTICATION_COMPLETED, {
      method: 'facebook',
      isNew: true,
    })
  }, [])

  return (
    <Query query={GET_VIEWER} skip={!cookies.get('token')}>
      {({ data = {}, client, loading: loadingViewer }) => {
        const { viewer: user } = data

        if (!Boolean(user) && !loadingViewer && hide) {
          // This happens when the user tries to register with facebook
          // while they have an outdated cookie in their token.
          // For now we will close the modal and hope that the user
          // tries to register again.
          cookies.remove('token')
          return hide()
        }

        return (
          <Mutation mutation={START_EMAIL_VERIFICATION}>
            {startUserEmailVerification => (
              <Mutation mutation={AGREE_WITH_TERMS_AND_PRIVACY_POLICY}>
                {agreeWithTermsAndPrivacyPolicy => (
                  <Formik
                    initialValues={{ email: user.email || '' }}
                    isInitialValid={({ validationSchema, initialValues }) =>
                      validationSchema.isValidSync(initialValues)
                    }
                    validationSchema={yup.object().shape({
                      email: yup
                        .string()
                        .email()
                        .required(t('generic.required')),
                    })}
                    onSubmit={async values => {
                      setLoading(true)

                      await startUserEmailVerification({
                        variables: {
                          input: {
                            userId: user.id,
                            email: values.email,
                            redirectTo: null,
                          },
                        },
                      })

                      if (marketingConsentGiven) {
                        await giveConsentMutation({
                          variables: {
                            input: {
                              consentType: 'MARKETING',
                              hasGivenConsent: true,
                            },
                          },
                        })
                      }

                      track(VERIFICATION_STARTED)
                      await agreeWithTermsAndPrivacyPolicy()
                      await client.resetStore()
                      setViewer(user)
                      onSignupComplete()
                    }}
                  >
                    {props => {
                      const {
                        values,
                        isValid,
                        handleChange,
                        handleSubmit,
                        handleReset,
                      } = props

                      return (
                        <form onSubmit={handleSubmit}>
                          <Input
                            placeholder={t('auth.signupFacebook.placeholder')}
                            type="email"
                            name="email"
                            id="email"
                            label={t(
                              user.email === null
                                ? 'auth.signupFacebook.label.unknown'
                                : 'auth.signupFacebook.label.email'
                            )}
                            value={values.email}
                            onChange={handleChange}
                            onReset={handleReset}
                          />

                          <StyledCheckbox
                            id="marketingConsent"
                            name="marketingConsent"
                            label={t('auth.consent.label')}
                            onChange={event =>
                              setMarketingConsentGiven(event.target.value)
                            }
                          />

                          <StyledButton
                            variant="success"
                            width="full"
                            type="submit"
                            disabled={!isValid}
                            loading={loading}
                          >
                            {t(
                              user.email === null
                                ? 'auth.signupFacebook.button.unknown'
                                : 'auth.signupFacebook.button.email'
                            )}
                          </StyledButton>
                        </form>
                      )
                    }}
                  </Formik>
                )}
              </Mutation>
            )}
          </Mutation>
        )
      }}
    </Query>
  )
}

SignupSocial.propTypes = {
  t: PropTypes.func.isRequired,
  onSignupComplete: PropTypes.func.isRequired,
  hide: PropTypes.func,
}

export default SignupSocial
